// One row of the alignment: a Block, the Manuscript nodes it was made from,
// and everything the Author can do to it from here.
//
// Both columns are cells of one grid row, so they cannot come apart. That is
// the whole reason this screen is a single list: two scrollers held in step by
// an event handler drift by a line as soon as the two sides have different
// heights, and a Block read against the wrong paragraph is exactly the mistake
// Review exists to catch.

import type { AlignmentPair, DroppedNode } from "@contract";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { PlaybackStatus } from "../../../components/audio/AudioProvider";
import { PlayButton } from "../../../components/audio/PlayButton";
import { cx } from "../../../components/cx";
import { WarnIcon } from "../../../components/icons";
import { DiffText } from "../../../components/review/DiffText";
import { PauseCueChip } from "../../../components/review/PauseCueChip";
import { BlockEditor } from "./BlockEditor";
import { nodeSpans, pairDiff } from "./diff";
import { readEmphasis } from "./emphasis";
import { ManuscriptColumn } from "./ManuscriptColumn";
import { showsManuscript, type ViewMode } from "./viewMode";

/**
 * A row's props are flat values and callbacks that take the row's index,
 * rather than one context object, so every prop of an untouched row is
 * identical between two renders of the list and `memo` can actually skip it.
 * A context object built in the list's render would be a new object each
 * time and would defeat the memo on every scroll frame.
 */
export interface AlignmentRowProps {
  readonly pair: AlignmentPair;
  readonly index: number;
  readonly mode: ViewMode;
  readonly focused: boolean;
  readonly editing: boolean;
  readonly editable: boolean;
  readonly saving: boolean;
  /** Playback of this Block's Sample, or null when no Voice is cast yet. */
  readonly sampleStatus: PlaybackStatus | null;
  readonly onEdit: (index: number) => void;
  readonly onDraftChange?: ((index: number, text: string) => void) | undefined;
  readonly onCommit: (index: number, text: string, viaKeyboard: boolean) => void;
  readonly onCancelEdit: (viaKeyboard: boolean) => void;
  readonly onPlay: () => void;
}

const ROW_PADDING = "px-9 py-5";

/**
 * The grid. Side by side is two equal cells with the 1px rule between them
 * drawn as a gap over the line colour, which is how the mockups get a hard
 * border without a shadow; the other two modes are one cell, so the script
 * gets the whole width rather than half of it and a blank column.
 */
const gridFor = (mode: ViewMode) =>
  showsManuscript(mode) ? "grid grid-cols-2 gap-px bg-line" : "grid grid-cols-1 bg-line";

export const AlignmentRow = memo(function AlignmentRow({
  pair,
  index,
  mode,
  focused,
  editing,
  editable,
  saving,
  sampleStatus,
  onEdit,
  onCommit,
  onDraftChange,
  onCancelEdit,
  onPlay,
}: AlignmentRowProps) {
  const { t } = useTranslation("review");
  // The Manuscript column is rendered while the Author edits too. It is what
  // they are editing against, and dropping it would collapse the two-column
  // grid to one and throw the text they were reading half the screen to the
  // left. The diff is cached, so drawing it during an edit costs a lookup.
  const diff = pairDiff(pair);
  const spans = showsManuscript(mode) ? nodeSpans(pair) : null;

  return (
    <div data-row="pair" data-block-index={pair.block.index} className={gridFor(mode)}>
      {spans ? (
        <div className={cx("min-w-0 bg-surface", ROW_PADDING)}>
          <ManuscriptColumn spans={spans} removals={diff.removals} />
        </div>
      ) : null}
      <div className={cx("flex min-w-0 flex-col gap-3 bg-surface", ROW_PADDING)}>
        {editing ? (
          <BlockEditor
            onDraftChange={(text) => onDraftChange?.(index, text)}
            initialText={pair.block.text}
            busy={saving}
            onCommit={(text, viaKeyboard) => onCommit(index, text, viaKeyboard)}
            onCancel={onCancelEdit}
          />
        ) : editable ? (
          // tabIndex -1: the row is the one tab stop (roving tabindex), and
          // `e` opens the editor from the keyboard. A tab stop per Block would
          // be eighty-seven of them in the longest Chapter of the sample Book.
          <button
            type="button"
            tabIndex={-1}
            title={t("editHint")}
            onClick={() => onEdit(index)}
            className="m-0 cursor-text whitespace-pre-line text-left font-serif text-book text-ink"
          >
            <BlockText pair={pair} mode={mode} />
          </button>
        ) : (
          <p className="m-0 whitespace-pre-line font-serif text-book text-ink">
            <BlockText pair={pair} mode={mode} />
          </p>
        )}
        <div className="flex items-center gap-2">
          {pair.block.pauseAfter === "none" ? null : <PauseCueChip kind={pair.block.pauseAfter} />}
          {pair.block.editedByAuthor ? (
            <span className="font-mono text-label text-accent">{t("row.edited")}</span>
          ) : null}
          <span className="flex-1" />
          {/* Only the focused row carries the control, so the Author never tabs
              through a Chapter's worth of play buttons on the way anywhere. */}
          {focused && sampleStatus ? (
            <PlayButton size="sm" status={sampleStatus} onClick={onPlay} />
          ) : null}
        </div>
      </div>
    </div>
  );
});

/**
 * Script only reads as the finished text; side by side marks what Refinement
 * added; inline shows both sides woven together. The parts handed over are
 * already filtered to the side being shown, so `DiffText` renders all of them
 * and no run is split by a part it was told to drop.
 */
function BlockText({ pair, mode }: { pair: AlignmentPair; mode: ViewMode }) {
  if (mode === "scriptOnly") return <>{pair.block.text}</>;
  const diff = pairDiff(pair);
  return <DiffText parts={mode === "inline" ? diff.parts : diff.scriptParts} />;
}

/**
 * A Manuscript node no Block covers. It is drawn as its own warn row rather
 * than a number in a summary, because "Refinement cannot silently lose a
 * paragraph" only holds if the Author can read the paragraph that went
 * missing and decide whether it mattered.
 */
export const DroppedNodeRow = memo(function DroppedNodeRow({
  node,
  mode,
}: {
  node: DroppedNode;
  mode: ViewMode;
}) {
  const { t } = useTranslation("review");
  const { text } = readEmphasis(node.text);
  return (
    <div data-row="dropped" data-node-id={node.id} className={gridFor(mode)}>
      <div className={cx("min-w-0 border-warn border-l-2 bg-warn-soft", ROW_PADDING)}>
        <p className="m-0 mb-2 flex items-center gap-2 font-mono text-label text-warn">
          <WarnIcon size={12} />
          {t("dropped.label")}
        </p>
        <p className="m-0 font-serif text-book text-ink">{text}</p>
      </div>
      {showsManuscript(mode) ? (
        <div className={cx("min-w-0 bg-surface", ROW_PADDING)}>
          <p className="m-0 font-mono text-label text-ink-3">{t("dropped.noBlock")}</p>
        </div>
      ) : null}
    </div>
  );
});
